/**
 * LabelGateDiscoveryReason - NOT a Prisma enum. This union exists only in
 * `shared` as the typed vocabulary for the 409 confirmation-gate response
 * (plan.md "Gate di conferma"), so the panel listing discovered rows can
 * switch on a reason code instead of matching free text. One value per gate
 * condition, in the same order the plan lists them:
 *
 * - MISSING_WEIGHT: `weightG` missing (or `quantity <= 0`).
 * - MISSING_INGREDIENT_LINK: a row is not linked to an `Ingredient`.
 * - MISSING_ALLERGEN_SUBSTANCE: a CONTAINS row has no `substanceId`.
 * - MISSING_BREAKDOWN: a compound ingredient has neither breakdown nor exemption.
 * - MISSING_ADDITIVE_NAME_OR_E_NUMBER: an `IngredientComponent` declares an
 *   `additiveFunction` (Allegato VII Parte C) but has neither `name` nor
 *   `additiveENumber` — a bare functional category ("Conservante" alone) is
 *   not a conforming ingredient declaration (art. 20 lett. a). Exempt:
 *   `MODIFIED_STARCH` (nota 2, no specific name or E number required) and any
 *   carry-over exempt component (never rendered in the first place).
 * - MISSING_LEGAL_NAME: `legalName` missing.
 * - MISSING_STORAGE_CONDITION: `isPerishable` without `storageCondition`.
 * - MISSING_STORAGE_TEMPERATURE: controlled storage without the applicable
 *   temperature limit/range.
 * - MISSING_SHELF_LIFE_DAYS: `isFreshPasta` without `shelfLifeDays`.
 * - MISSING_ALCOHOL_BY_VOLUME: `isBeverage` without `alcoholByVolume`.
 * - MISSING_GLAZE_PERCENT: `isGlazedFrozen` without `glazePercent`.
 * - MISSING_DEFROST_EXCEPTION_NOTE: `defrostDeclaration` in
 *   {FREEZING_TECH_REQUIRED, DEFROST_NO_IMPACT} without `defrostExceptionNote`.
 * - MISSING_MILK_FOR_PROTECTED_DAIRY_NAME: a protected dairy denomination
 *   without milk among ingredients and allergens.
 * - RECIPE_GRAPH_TOO_DEEP: the recipe graph exceeds the depth bound.
 * - RECIPE_GRAPH_TOO_LARGE: the recipe expands into more tree paths than the
 *   node bound (a truncated ingredient list would be an omission).
 * - UNLINKED_RECIPE_INGREDIENT: a recipe row is not linked to an `Ingredient`,
 *   so its allergens cannot be verified (allergens are derived on-read from
 *   `Ingredient`/`Lot`, never declared on the recipe itself).
 * - UNDECLARED_INGREDIENT_ALLERGENS: a recipe row (or an `INGREDIENT`
 *   confirmation subject) IS linked to an `Ingredient`, but nobody has ever
 *   declared its allergen profile — `Ingredient.isCompound` is still `null`
 *   (the tri-state "nobody has looked" signal) AND it carries zero
 *   `IngredientAllergen` rows. Distinct from `UNLINKED_RECIPE_INGREDIENT`:
 *   the row IS linked, so that reason would be a lie here. Also distinct
 *   from a curated ingredient that was explicitly declared to have zero
 *   allergens (e.g. salt) — that case does NOT raise this reason. Spec
 *   2026-08-01 "Il gate allergeni": without this reason, bulk-linking every
 *   recipe row to a freshly migrated, uncurated `Ingredient` would silently
 *   report `allergensComplete: true` with zero allergens verified.
 * - MISSING_RECIPE_INGREDIENTS: a recipe has no ingredient or sub-recipe rows
 *   at all, so its public register sheet would otherwise contain only a name
 *   and a code with no ingredient list.
 * - INGREDIENT_COMPOSITION_TOO_DEEP: the linked-component composition of an
 *   ingredient exceeds the depth or size bound, or loops back on itself, so
 *   its printed ingredient list and allergen set would be truncated.
 * - UNDECLARED_COMPOSITION: WARNING-ONLY (never in `discoveredLines`, only in
 *   the preview's `warningLines`): `Ingredient.isCompound` is still `null`
 *   (nobody declared simple vs compound) with no components and no exemption.
 *   We cannot know it is compound, so it must not block printing (unlike
 *   `MISSING_BREAKDOWN`, reserved to `isCompound: true`).
 */
export const LABEL_GATE_DISCOVERY_REASONS = [
  'MISSING_WEIGHT',
  'MISSING_INGREDIENT_LINK',
  'MISSING_ALLERGEN_SUBSTANCE',
  'MISSING_BREAKDOWN',
  'MISSING_ADDITIVE_NAME_OR_E_NUMBER',
  'MISSING_LEGAL_NAME',
  'MISSING_STORAGE_CONDITION',
  'MISSING_STORAGE_TEMPERATURE',
  'MISSING_SHELF_LIFE_DAYS',
  'MISSING_ALCOHOL_BY_VOLUME',
  'MISSING_GLAZE_PERCENT',
  'MISSING_DEFROST_EXCEPTION_NOTE',
  'MISSING_MILK_FOR_PROTECTED_DAIRY_NAME',
  'RECIPE_GRAPH_TOO_DEEP',
  'RECIPE_GRAPH_TOO_LARGE',
  'UNLINKED_RECIPE_INGREDIENT',
  'UNDECLARED_INGREDIENT_ALLERGENS',
  'MISSING_RECIPE_INGREDIENTS',
  'UNDECLARED_COMPOSITION',
  'INGREDIENT_COMPOSITION_TOO_DEEP',
] as const;
export type LabelGateDiscoveryReason = (typeof LABEL_GATE_DISCOVERY_REASONS)[number];
